import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useBusinessEntity } from '@/hooks/business-entity'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import { BuildingIcon, ChevronDownIcon } from 'lucide-react'
import { Link } from 'react-router'
import { useAuth } from '../../hooks/useAuth'
import { Button } from '../ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { Separator } from '../ui/separator'

export function ProfileMenu() {
  // Hooks
  const { user, isAuthenticated, isLoading, logout } = useAuth()
  const { businessEntity, isRequired } = useBusinessEntity()

  // Loading
  if (isLoading) return <div>Loading... </div>

  // Not authenticated, show login
  if (!isAuthenticated) return <Link to={buildPath('/dashboard')}>Login</Link>

  return (
    <div className="flex h-5 items-center gap-2 md:gap-4">
      {isRequired && <BusinessEntityMenu />}

      {businessEntity && isRequired && (
        <Separator orientation="vertical" className="hidden md:block" />
      )}

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            aria-label={`User menu, ${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`}
          >
            <Avatar>
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuGroup>
            <DropdownMenuLabel>
              Welcome, {user?.firstName || user?.email}
            </DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to={buildPath('/profile')}>Profile</Link>
            </DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem variant="destructive" onClick={logout}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function BusinessEntityMenu() {
  const { businessEntity, businessEntities, selectBusinessEntity } =
    useBusinessEntity()

  // No business entities, or just one, nothing to show
  if (!businessEntities || businessEntities.length === 0) return null

  const ButtonOrText = businessEntities.length > 1 ? Button : 'div'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ButtonOrText
          variant="ghost"
          className="inline-flex items-center gap-2 text-sm"
        >
          <BuildingIcon className="size-5" />
          <span className="max-w-[11rem] min-w-0 truncate font-semibold md:max-w-none">
            {businessEntity
              ? format.CarrierName(businessEntity)
              : 'Select a Company'}
          </span>
          {businessEntities.length > 1 && <ChevronDownIcon />}
        </ButtonOrText>
      </DropdownMenuTrigger>
      {businessEntities.length > 1 && (
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuGroup>
            <DropdownMenuLabel>Select a Company</DropdownMenuLabel>
            {businessEntities.map((entity) => (
              <DropdownMenuItem
                key={entity.id}
                onClick={() => selectBusinessEntity(entity.id)}
              >
                {format.CarrierName(entity)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
}
